import { lazy, ReactNode, Suspense, useMemo } from "react";

import { useMatch } from "common/router/hooks";
import { StorefrontThemeLayout } from "common/types/clinics";
import { stringToValidValue } from "common/utils/strings";
import { Optional } from "common/utils/types";
import { parseNumber } from "common/utils/numbers";

import Page from "client/common/components/layouts/Page";
import { useClinic, useStorefrontThemeColorsQuery } from "client/clinics/hooks";
import { Paths } from "client/routes";
import { StorefrontThemeImage } from "client/clinics/types";

import HomeThemeCutout from "./HomeThemeCutout";
import HomeThemeDefault from "./HomeThemeDefault";
import HomeThemeHero from "./HomeThemeHero";
import { HomeThemeProps, StorefrontThemeConfig } from "../types";

import styles from "../styles/HomePage.module.less";

const HomeThemeBondVet = lazy(() => import("./custom/HomeThemeBondVet"));
const HomeThemeThrive = lazy(() => import("./custom/HomeThemeThrive"));
const HomeThemeGoodVets = lazy(() => import("./custom/HomeThemeGoodVets"));

type LazyThemeProps = {
  children: ReactNode;
};

const LazyTheme = ({ children }: LazyThemeProps) => {
  return <Suspense fallback={<div className={styles.lazyPlaceholder} />}>{children}</Suspense>;
};

const HomePage = () => {
  const clinic = useClinic();
  const match = useMatch<Paths["home"]["root"]>();

  const { data: storefrontThemeColorOptions, isLoading: isLoadingColors } =
    useStorefrontThemeColorsQuery({ enabled: !!match.search.layout });

  const storefrontThemeConfig: StorefrontThemeConfig = useMemo(() => {
    const {
      storefront_theme_layout: layout,
      storefront_theme_color: color,
      storefront_theme_logo: logo,
      storefront_theme_cutout_image: cutoutImage,
      storefront_theme_hero_image: heroImage,
    } = clinic;
    // support query params for clinic app previews
    const searchLayout =
      match.search.layout &&
      stringToValidValue<StorefrontThemeLayout, undefined>(
        match.search.layout,
        Object.values(StorefrontThemeLayout),
        undefined,
      );
    const searchColorId = parseNumber(match.search.color, undefined);

    const displayLayout = searchLayout || layout;

    const layoutToClinicConfiguredImage: Partial<
      Record<StorefrontThemeLayout, Optional<StorefrontThemeImage>>
    > = {
      [StorefrontThemeLayout.CUTOUT]: cutoutImage,
      [StorefrontThemeLayout.HERO]: heroImage,
    };
    const image = layoutToClinicConfiguredImage[displayLayout];
    return {
      layout: displayLayout,
      color:
        storefrontThemeColorOptions?.find((opt) => searchColorId && opt.id === searchColorId) ||
        color,
      logo: match.search.logo || logo?.medium_url,
      image: match.search.image ? { image: match.search.image } : image,
    };
  }, [clinic, match.search, storefrontThemeColorOptions]);

  if (isLoadingColors) {
    return null;
  }

  const layoutToRenderHomeTheme: Record<
    StorefrontThemeLayout,
    (props: HomeThemeProps) => ReactNode
  > = {
    [StorefrontThemeLayout.DEFAULT]: (p) => <HomeThemeDefault {...p} />,
    [StorefrontThemeLayout.CUTOUT]: (p) => <HomeThemeCutout {...p} />,
    [StorefrontThemeLayout.HERO]: (p) => <HomeThemeHero {...p} />,
    // Custom Themes
    // lazy to not load for every bundle load even though they
    // are only for particular groups
    [StorefrontThemeLayout.BONDVET]: (p) => (
      <LazyTheme>
        <HomeThemeBondVet {...p} />
      </LazyTheme>
    ),
    [StorefrontThemeLayout.THRIVE]: (p) => (
      <LazyTheme>
        <HomeThemeThrive {...p} />
      </LazyTheme>
    ),
    [StorefrontThemeLayout.GOODVETS]: (p) => (
      <LazyTheme>
        <HomeThemeGoodVets {...p} />
      </LazyTheme>
    ),
  };

  return (
    // no header to let the themes handle them
    <Page Header={null} className={styles.page}>
      {layoutToRenderHomeTheme[storefrontThemeConfig.layout]({ storefrontThemeConfig })}
    </Page>
  );
};

export default HomePage;
